import { apiClient } from '@borradh-workspace/api-client';
import type { QueryConfig } from '@borradh-workspace/api-client/react-query';
import {
  type ConnectionHealthResponse,
  connectionHealthResponseSchema,
} from '@borradh-workspace/contracts';
import { queryOptions, useQuery } from '@tanstack/react-query';

import { queryKeys } from '@/lib/query-keys';

/**
 * The single read for "can we still act for this customer".
 *
 * Backs the home banner and the integrations dashboard, so the two cannot
 * disagree. Before this, the dashboard derived a status per provider from four
 * different column shapes and nothing on home mentioned connections at all —
 * which is how a WhatsApp number that had taken 61 customer messages in 30 days
 * stayed dead without anyone noticing.
 */
export const getConnectionHealthQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.integrations.connectionHealth(),
    queryFn: () =>
      apiClient.get<ConnectionHealthResponse>(
        'integrations/connection-health',
        { schema: connectionHealthResponseSchema }
      ),
    staleTime: 60 * 1000,
  });

type UseGetConnectionHealthOptions = {
  queryConfig?: QueryConfig<typeof getConnectionHealthQueryOptions>;
};

export const useGetConnectionHealth = ({
  queryConfig,
}: UseGetConnectionHealthOptions = {}) => {
  const query = useQuery({
    ...getConnectionHealthQueryOptions(),
    ...queryConfig,
  });

  return {
    connections: query.data?.connections ?? [],
    /** Only what a human needs to act on — never the healthy rows. */
    problems: query.data?.problems ?? [],
    // Defaults to healthy while loading and on error: a failed lookup must not
    // render as "your accounts are broken".
    allHealthy: query.data?.allHealthy ?? true,
    lastCheckedAt: query.data?.lastCheckedAt ?? null,
    isLoading: query.isPending,
    isError: query.isError,
    refetch: query.refetch,
  };
};
